import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, UserCheck, X } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const ROLES = [["user", "Χρήστης (Γραμματεία)"], ["sector_head_mono", "Υπεύθυνος/η Τομέα Μονομελούς"], ["sector_head_poly", "Υπεύθυνος/η Προσδιορισμού Τομέα Πολυμελούς"],
  ["composition_president", "Πρόεδρος Πολυμελούς Σύνθεσης"], ["judge", "Δικαστής"], ["admin", "Διαχειριστής"]];

/** Admin → Χρήστες: αιτήσεις εγγραφής σε αναμονή (έγκριση με ρόλο / απόρριψη). */
const PendingRegistrationsCard = ({ onChanged }) => {
  const [pending, setPending] = useState([]);
  const [roles, setRoles] = useState({});
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const r = await axios.get(`${API}/api/admin/registrations`); setPending(r.data.pending || []); }
    catch (e) { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (u, action) => {
    setBusy(u.id);
    try {
      await axios.post(`${API}/api/admin/registrations/${u.id}/${action}`, action === "approve" ? { role: roles[u.id] || "user" } : {});
      toast.success(action === "approve" ? `Ο χρήστης ${u.username} εγκρίθηκε και μπορεί πλέον να συνδεθεί` : `Η αίτηση του ${u.username} απορρίφθηκε`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
    finally { setBusy(""); }
  };

  if (!loading && !pending.length) return null;
  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="pending-registrations-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><UserCheck className="w-5 h-5 text-amber-700" />Αιτήσεις Εγγραφής σε αναμονή <Badge className="bg-amber-600" data-testid="pending-count">{pending.length}</Badge></CardTitle>
        <CardDescription>Οι χρήστες εγγράφηκαν από τη σελίδα σύνδεσης. Επιλέξτε ρόλο και εγκρίνετε — μόνο τότε θα μπορούν να συνδεθούν.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
          <div className="divide-y rounded-lg border bg-white">
            {pending.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`pending-row-${u.username}`}>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-medium text-slate-900">{u.full_name} <span className="text-slate-500 font-normal">({u.username})</span></div>
                  <div className="text-xs text-slate-500">{u.email}{u.registration_position ? ` · ${u.registration_position}` : ""} · {u.registered_at ? new Date(u.registered_at).toLocaleString("el-GR") : ""}</div>
                </div>
                <Select value={roles[u.id] || "user"} onValueChange={(v) => setRoles({ ...roles, [u.id]: v })}>
                  <SelectTrigger className="w-72 h-9" data-testid={`pending-role-${u.username}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy === u.id} onClick={() => act(u, "approve")} data-testid={`pending-approve-${u.username}`}><Check className="w-4 h-4 mr-1" />Έγκριση</Button>
                <Button size="sm" variant="outline" className="text-red-600" disabled={busy === u.id} onClick={() => window.confirm(`Απόρριψη και διαγραφή της αίτησης του ${u.username};`) && act(u, "reject")} data-testid={`pending-reject-${u.username}`}><X className="w-4 h-4 mr-1" />Απόρριψη</Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingRegistrationsCard;
